import { CreditCardIcon, LandmarkIcon, ReceiptTextIcon } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import type { ReactNode } from 'react'

import { CheckSlip } from './CheckSlip'
import { PaperSheet } from './PaperSheet'
import { SceneBadge } from './SceneBadge'

const LOOP_SECONDS = 6
const ENTRY_STAGGER_SECONDS = 0.12
const BADGE_STAGGER = 0.08
// Slide in, show the cross, fade aside, hold, leave.
const REFUSED_TIMES = [0, 0.34, 0.42, 0.5, 0.56, 0.64, 0.92, 1]

const ACCEPTED_DOCUMENTS = [
  {
    key: 'bank',
    drawing: <PaperSheet label="Bank statement" icon={LandmarkIcon} />,
  },
  {
    key: 'card',
    drawing: <PaperSheet label="Credit card" icon={CreditCardIcon} />,
  },
  { key: 'check', drawing: <CheckSlip /> },
]

// The three document kinds get a check; an invoice slides in and is turned away.
export const SupportedDocumentsScene = () => {
  const reduceMotion = useReducedMotion() ?? false
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex shrink-0 scale-[0.62] items-center gap-4 sm:scale-100">
        {ACCEPTED_DOCUMENTS.map(({ key, drawing }, order) => (
          <AcceptedDocument key={key} order={order} reduceMotion={reduceMotion}>
            {drawing}
          </AcceptedDocument>
        ))}
        <span className="h-28 w-px bg-border" />
        <RefusedDocument reduceMotion={reduceMotion} />
      </div>
    </div>
  )
}

interface AcceptedDocumentProps {
  order: number
  reduceMotion: boolean
  children: ReactNode
}

const AcceptedDocument = ({
  order,
  reduceMotion,
  children,
}: AcceptedDocumentProps) => {
  const badgeAt = 0.08 + order * BADGE_STAGGER
  return (
    <motion.div
      className="relative"
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: order * ENTRY_STAGGER_SECONDS, ease: 'easeOut' }}
    >
      {children}
      <motion.span
        className="absolute -top-2.5 -right-2.5"
        initial={{ scale: reduceMotion ? 1 : 0 }}
        animate={reduceMotion ? { scale: 1 } : { scale: [0, 0, 1, 1, 0] }}
        transition={{
          duration: LOOP_SECONDS,
          times: [0, badgeAt, badgeAt + 0.05, 0.9, 0.96],
          ease: 'backOut',
          repeat: Number.POSITIVE_INFINITY,
        }}
      >
        <SceneBadge kind="accepted" />
      </motion.span>
    </motion.div>
  )
}

const refusedLoop = {
  duration: LOOP_SECONDS,
  times: REFUSED_TIMES,
  repeat: Number.POSITIVE_INFINITY,
}

const RefusedDocument = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className="relative flex flex-col items-center gap-2">
    <motion.div
      initial={reduceMotion ? { opacity: 0.45 } : { opacity: 0, x: 32 }}
      animate={
        reduceMotion
          ? { opacity: 0.45, filter: 'grayscale(1)' }
          : {
              opacity: [0, 0, 1, 1, 1, 0.45, 0.45, 0],
              x: [32, 32, 0, 0, 0, 0, 0, 0],
              filter: [0, 0, 0, 0, 0, 1, 1, 1].map(
                (amount) => `grayscale(${amount})`,
              ),
            }
      }
      transition={{ ...refusedLoop, ease: 'easeOut' }}
    >
      <PaperSheet label="Invoice" icon={ReceiptTextIcon} />
    </motion.div>
    <RefusedMarks reduceMotion={reduceMotion} />
  </div>
)

const RefusedMarks = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <>
    <motion.span
      className="absolute -top-2.5 -right-2.5"
      initial={{ scale: reduceMotion ? 1 : 0 }}
      animate={
        reduceMotion ? { scale: 1 } : { scale: [0, 0, 0, 0, 1, 1, 1, 0] }
      }
      transition={{ ...refusedLoop, ease: 'backOut' }}
    >
      <SceneBadge kind="refused" />
    </motion.span>
    <motion.span
      className="text-[11px] font-medium text-blue-5"
      initial={{ opacity: reduceMotion ? 1 : 0 }}
      animate={
        reduceMotion ? { opacity: 1 } : { opacity: [0, 0, 0, 0, 0, 1, 1, 0] }
      }
      transition={refusedLoop}
    >
      Not processed
    </motion.span>
  </>
)
